import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useEffect, type ReactNode } from 'react'
import { View } from 'react-native'

import { HozanimatedLoader } from '@/src/elements/HozanimatedLoader/HozanimatedLoader'
import { GradientTypography } from '@/src/elements/text/gradient'
import { getMeQueryOptions } from '@/src/features/auth/services/useMeQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useWizardMutation } from '../../../services/useWizardMutation'
import { useWizardSettingsMutation } from '../../../services/useWizardSettingsMutation'
import { useWizardStore } from '../../../stores/useWizardStore'

const TIME_BEFORE_REDIRECT = 3000

export const EndStep = (): ReactNode => {
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { replace } = useRouter()
  const { tags, gender, birthdate, location, resetWizard } = useWizardStore()
  const { mutateAsync: wizardMutation } = useWizardMutation()
  const { mutateAsync: settingsMutation } = useWizardSettingsMutation()
  const queryClient = useQueryClient()

  useEffect(() => {
    let isCancelled = false
    let redirectTimeout: ReturnType<typeof setTimeout> | null = null

    async function update() {
      await Promise.all([
        wizardMutation({ tags }),
        settingsMutation({
          gender,
          birthdate: birthdate !== undefined ? getDateWithoutTimezone(birthdate)?.format('YYYY-MM-DD') : birthdate,
          location,
        }),
      ])

      queryClient.invalidateQueries({ queryKey: getMeQueryOptions().queryKey })

      if (isCancelled) return

      redirectTimeout = setTimeout(() => {
        resetWizard()
        replace(routes.communities())
      }, TIME_BEFORE_REDIRECT)
    }

    update()

    return () => {
      isCancelled = true
      if (redirectTimeout) {
        clearTimeout(redirectTimeout)
      }
    }
  }, [birthdate, gender, location, queryClient, replace, resetWizard, routes, settingsMutation, tags, wizardMutation])

  return (
    <View testID={TEST_ID.ME.WIZARD.END} className="flex flex-1 items-center justify-center gap-10">
      <HozanimatedLoader />

      <GradientTypography
        center
        size="lg"
        black
        colors="primary"
        classNameWrapper="native:h-16 w-full"
        className="native:w-full text-center font-black"
      >
        {t('user.wizard.complete')}
      </GradientTypography>
    </View>
  )
}
